import React, { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ladeDokumentHoch, ladeDokumentHerunter, loescheDokument } from 'modules/investitionen/features/investitionenSlice';
import { formatiereGroesse } from 'modules/investitionen/features/berechnung';

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB pro Datei

const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

/** Gruppiert Dokumente absteigend nach Jahr ("Ohne Jahr" zuletzt). */
const gruppiereNachJahr = (dokumente) => {
  const map = {};
  dokumente.forEach((d) => {
    const jahr = d.jahr || 'Ohne Jahr';
    (map[jahr] = map[jahr] || []).push(d);
  });
  return Object.entries(map).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
};

/**
 * Verwaltet die verschluesselten Dokumente eines Bereichs, gruppiert nach Jahr
 * (fuer die Steuer). Upload verschluesselt im Browser, Download entschluesselt
 * dort wieder.
 */
export default function DokumentePanel({ investitionUuid, dokumente }) {
  const dispatch = useDispatch();
  const eingabe = useRef(null);
  const [jahr, setJahr] = useState(String(new Date().getFullYear()));
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  const gruppen = useMemo(() => gruppiereNachJahr(dokumente), [dokumente]);

  const hochladen = async (event) => {
    const dateien = Array.from(event.target.files || []);
    event.target.value = '';
    setFehler(null);
    for (const file of dateien) {
      if (file.size > MAX_BYTES) {
        setFehler(`„${file.name}" ist größer als ${formatiereGroesse(MAX_BYTES)} und wurde übersprungen.`);
        continue;
      }
      setLaedt(true);
      try {
        await dispatch(ladeDokumentHoch({ file, investitionUuid, jahr: jahr.trim() || null })).unwrap();
      } catch (ex) {
        setFehler(`Hochladen von „${file.name}" fehlgeschlagen.`);
      }
    }
    setLaedt(false);
  };

  const loeschen = (d) => {
    if (window.confirm(`Dokument „${d.name}" löschen?`)) dispatch(loescheDokument(d.uuid));
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">Steuerjahr</label>
          <input
            type="number"
            value={jahr}
            onChange={(e) => setJahr(e.target.value)}
            placeholder="z. B. 2025"
            className="w-28 rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => eingabe.current?.click()}
          disabled={laedt}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
        >
          {laedt ? 'Verschlüssle & lade hoch…' : '+ Dokument hochladen'}
        </button>
        <input ref={eingabe} type="file" multiple onChange={hochladen} className="hidden" />
        <span className="text-xs text-ink-muted">Max. {formatiereGroesse(MAX_BYTES)} · verschlüsselt gespeichert</span>
      </div>

      {fehler && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

      {dokumente.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-paper-soft p-6 text-center text-sm text-ink-muted">
          Noch keine Dokumente. Lade z. B. Jahressteuerbescheinigungen oder Kaufbelege hoch.
        </p>
      ) : (
        <div className="space-y-4">
          {gruppen.map(([jahrLabel, liste]) => (
            <div key={jahrLabel}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{jahrLabel}</h4>
              <ul className="divide-y divide-line rounded-xl border border-line bg-paper">
                {liste.map((d) => (
                  <li key={d.uuid} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="text-xl" aria-hidden="true">📄</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">{d.name}</p>
                      <p className="text-xs text-ink-muted">{formatiereGroesse(d.groesseOriginal)} · {datum(d.createdAt)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => dispatch(ladeDokumentHerunter(d))}
                      className="rounded-lg px-3 py-1.5 text-sm text-brand-600 hover:bg-brand-600/10"
                    >
                      Herunterladen
                    </button>
                    <button
                      type="button"
                      onClick={() => loeschen(d)}
                      className="rounded-lg px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      Löschen
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
